import React, { useState } from "react";
import "./App.css";
import { Provider } from "./components/ui/provider";
import { Header } from "./components/Header";
import { WelcomeScreen } from "./components/screens/WelcomeScreen";
import { AccessibilitySettingsScreen } from "./components/screens/AccessibilitySettingsScreen";
import { CourseScreen } from "./components/screens/CourseScreen";
import { CourseSelectionScreen } from "./components/screens/CourseSelectionScreen";
import { QuizManagerScreen } from "./components/screens/QuizManagerScreen";
import { Flex, Box } from "@chakra-ui/react";
import { AccessibilityProvider } from "./context/AccessibilityContext";
import { ThemeManager } from "./context/ThemeManager";
import { AuthProvider } from "./context/AuthContext";
import { FeedbackScreen } from "./components/screens/FeedbackScreen";
import { PricingScreen } from "./components/screens/PricingScreen";
import navigationSound from "./assets/sounds/navigation.mp3";
import { useAccessibility } from "./context/AccessibilityContext";
import LoginComponent from "./components/LoginComponent";
import RegisterComponent from "./components/RegisterComponent";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import ForgotPasswordComponent from "./components/ForgotPasswordComponent";
import SonicCodeEngine from "./components/SonicCodeEngine";

function AppContent() {
  const location = useLocation();
  const [screen, setScreen] = useState(location.state?.screen || 1);
  const [previousScreen, setPreviousScreen] = useState(null);
  const [userName, setUserName] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("python");
  const { soundEnabled } = useAccessibility();

  const playSound = (sound) => {
    if (soundEnabled) {
      new Audio(sound).play();
    }
  };

  const handleNext = (name) => {
    if (typeof name === "string" && name) {
      setUserName(name);
    }
    setScreen((prevScreen) => prevScreen + 1);
  };

  const handleFinish = () => {
    setScreen(1);
    setUserName("");
  };

  const handleBack = () => {
    playSound(navigationSound);
    if (previousScreen) {
      setScreen(previousScreen);
      setPreviousScreen(null);
    } else {
      setScreen((prevScreen) => Math.max(1, prevScreen - 1));
    }
  };

  const navigateToAccessibility = () => {
    playSound(navigationSound);
    setPreviousScreen(screen);
    setScreen(2);
  };

  const renderScreen = () => {
    switch (screen) {
      case 1:
        return <WelcomeScreen onNext={handleNext} />;
      case 2:
        return (
          <AccessibilitySettingsScreen
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <CourseSelectionScreen
            onNext={(courseId) => {
              setSelectedCourse(courseId);
              setScreen(4);
            }}
            onBack={handleBack}
            onViewPricing={() => {
              setPreviousScreen(screen);
              setScreen(7);
            }}
          />
        );
      case 4:
        return (
          <CourseScreen
            courseId={selectedCourse}
            onNext={() => setScreen(5)}
            onBack={() => setScreen(3)}
            onNavigateToAccessibility={navigateToAccessibility}
          />
        );
      case 5:
        return (
          <QuizManagerScreen
            courseId={selectedCourse}
            userName={userName}
            onNavigateToAccessibility={navigateToAccessibility}
            onNext={() => setScreen(6)}
            onBack={() => setScreen(4)}
          />
        );
      case 6:
        return <FeedbackScreen onFinish={handleFinish} />;
      case 7:
        return (
          <PricingScreen
            onBack={() => {
              if (previousScreen) {
                setScreen(previousScreen);
                setPreviousScreen(null);
              } else {
                setScreen(3);
              }
            }}
            onSelectPlan={(planId) => {
              console.log("Selected plan:", planId);
              // Future: Integrate with payment gateway
              alert(`You selected plan: ${planId}. Payment integration coming soon!`);
            }}
          />
        );
      default:
        return <WelcomeScreen onNext={handleNext} />;
    }
  };

  return (
    <Box className="App">
      <Flex
        as="main"
        flex="1"
        direction="column"
        justifyContent="center"
        alignItems="center"
        p={4}
        width="100%"
      >
        {renderScreen()}
      </Flex>
    </Box>
  );
}

function LoginRoute() {
  const navigate = useNavigate();
  return (
    <LoginComponent
      onRegisterClick={() => navigate("/register")}
      onForgotPasswordClick={() => navigate("/forget")}
    />
  );
}

function App() {
  return (
    <Provider>
      <AuthProvider>
        <AccessibilityProvider>
          <ThemeManager />
          <BrowserRouter>
            <Header />
            <Routes>
              <Route path="/login" element={<LoginRoute />} />
              <Route path="/register" element={<RegisterComponent />} />
              <Route path="/forget" element={<ForgotPasswordComponent />} />
              <Route path="/sonic" element={<SonicCodeEngine />} />
              <Route path="/home" element={<AppContent />} />
              <Route path="/" element={<AppContent />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AccessibilityProvider>
      </AuthProvider>
    </Provider>
  );
}

export default App;
